/**
 * Reporting periods and the time series drawn over them.
 *
 * A period is a run of whole calendar days, and every page compares it with
 * the run of equal length just before it — the same arithmetic the metrics
 * API uses for `compareToPreviousPeriod`, so a delta on a card and the dashed
 * line on its chart are about the same days.
 *
 * Buckets are UTC and keyed the way the API keys them (`%Y-%m-%d`, ISO
 * `%G-W%V`, `%Y-%m`), so a missing bucket can be told from a zero one and
 * filled in rather than silently skipped by the chart.
 */
import type { TrendPoint } from './TrendChart';

export type RangeKey = '1d' | '7d' | '30d' | '90d' | '365d' | 'custom';
export type Granularity = 'day' | 'week' | 'month';

export const RANGE_PRESETS: Exclude<RangeKey, 'custom'>[] = ['1d', '7d', '30d', '90d', '365d'];

/** Whole days each preset covers, today included. "Last 24 hours" spans yesterday and today. */
const PRESET_DAYS: Record<Exclude<RangeKey, 'custom'>, number> = { '1d': 2, '7d': 7, '30d': 30, '90d': 90, '365d': 365 };

const DAY = 86_400_000;

export interface AnalyticsPeriod {
  key: RangeKey;
  /** YYYY-MM-DD, inclusive. */
  startDate: string;
  /** YYYY-MM-DD, inclusive. */
  endDate: string;
  /** The same number of days, ending the day before `startDate`. */
  previousStartDate: string;
  previousEndDate: string;
  days: number;
}

const pad = (n: number) => String(n).padStart(2, '0');

/** YYYY-MM-DD of a UTC timestamp. */
export const utcDay = (ms: number) => {
  const d = new Date(ms);
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
};

/** Milliseconds of a YYYY-MM-DD at 00:00 UTC. */
export const dayMs = (day: string) => {
  const [y, m, d] = day.split('-').map(Number);
  return Date.UTC(y, (m || 1) - 1, d || 1);
};

/** Today in the viewer's calendar, as YYYY-MM-DD. */
export const localToday = (now: Date = new Date()) => `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;

/**
 * The days a range covers and the days it is compared with. A custom range
 * is taken as given (and put in order if reversed); a preset ends today.
 */
export function resolvePeriod(key: RangeKey, options: { now?: Date; start?: string; end?: string } = {}): AnalyticsPeriod {
  let startMs: number;
  let endMs: number;
  if (key === 'custom' && options.start && options.end) {
    const a = dayMs(options.start);
    const b = dayMs(options.end);
    startMs = Math.min(a, b);
    endMs = Math.max(a, b);
  } else {
    const preset = key === 'custom' ? '30d' : key;
    endMs = dayMs(localToday(options.now));
    startMs = endMs - (PRESET_DAYS[preset] - 1) * DAY;
  }
  const days = Math.round((endMs - startMs) / DAY) + 1;
  const previousEndMs = startMs - DAY;
  const previousStartMs = previousEndMs - (days - 1) * DAY;
  return {
    key,
    startDate: utcDay(startMs),
    endDate: utcDay(endMs),
    previousStartDate: utcDay(previousStartMs),
    previousEndDate: utcDay(previousEndMs),
    days,
  };
}

/** Whole days a preset covers. */
export const presetDays = (key: Exclude<RangeKey, 'custom'>): number => PRESET_DAYS[key];

/**
 * The first day a plan's history window lets a report read, as YYYY-MM-DD:
 * `maxDays` whole days ending today. Undefined when the window is unlimited.
 */
export function earliestReadableDay(maxDays: number | undefined, now: Date = new Date()): string | undefined {
  if (!maxDays || maxDays < 0) return undefined;
  return utcDay(dayMs(localToday(now)) - (Math.max(1, maxDays) - 1) * DAY);
}

/** Whether a range reaches further back than the plan's history window. */
export function exceedsHistory(
  value: { key: RangeKey; start?: string; end?: string },
  maxDays: number | undefined,
  now: Date = new Date(),
): boolean {
  if (!maxDays || maxDays < 0) return false;
  if (value.key !== 'custom') return PRESET_DAYS[value.key] > maxDays;
  const earliest = earliestReadableDay(maxDays, now);
  if (!value.start || !earliest) return false;
  const start = value.end && value.end < value.start ? value.end : value.start;
  return start < earliest;
}

/**
 * The period to read under a history window: the chosen one when it fits,
 * otherwise the longest preset that does. A page that opens on "last 90 days"
 * opens on "last 30 days" for a plan that keeps 30.
 */
export function fitPeriod<T extends { key: RangeKey; start?: string; end?: string }>(
  value: T,
  maxDays: number | undefined,
  presets: Exclude<RangeKey, 'custom'>[] = RANGE_PRESETS,
  now: Date = new Date(),
): T | { key: RangeKey } {
  if (!exceedsHistory(value, maxDays, now)) return value;
  const fitting = presets.filter((p) => PRESET_DAYS[p] <= (maxDays as number));
  const longest = fitting.sort((a, b) => PRESET_DAYS[b] - PRESET_DAYS[a])[0];
  return { key: longest ?? presets[0] ?? '7d' };
}

/** Days up to six weeks, weeks up to half a year, months beyond. */
export function defaultGranularity(days: number): Granularity {
  if (days <= 45) return 'day';
  if (days <= 190) return 'week';
  return 'month';
}

/** ISO 8601 week key of a UTC timestamp, as Mongo's `%G-W%V` writes it: "2026-W37". */
export function isoWeekKey(ms: number): string {
  const d = new Date(ms);
  const weekday = (d.getUTCDay() + 6) % 7; // Monday = 0
  // The Thursday of this week decides which year the week belongs to.
  const thursday = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() - weekday + 3);
  const year = new Date(thursday).getUTCFullYear();
  const firstThursday = (() => {
    const jan4 = Date.UTC(year, 0, 4);
    const w = (new Date(jan4).getUTCDay() + 6) % 7;
    return jan4 - w * DAY + 3 * DAY;
  })();
  const week = 1 + Math.round((thursday - firstThursday) / (7 * DAY));
  return `${year}-W${pad(week)}`;
}

const keyOf = (ms: number, granularity: Granularity) =>
  granularity === 'month' ? utcDay(ms).slice(0, 7) : granularity === 'week' ? isoWeekKey(ms) : utcDay(ms);

/** Every bucket from `startDate` to `endDate`, in order, each once. */
export function bucketKeys(startDate: string, endDate: string, granularity: Granularity): string[] {
  const keys: string[] = [];
  const end = dayMs(endDate);
  for (let ms = dayMs(startDate); ms <= end; ms += DAY) {
    const key = keyOf(ms, granularity);
    if (keys[keys.length - 1] !== key) keys.push(key);
  }
  return keys;
}

/** The value of each bucket, zero where the API returned none. */
export function fillSeries(rows: ReadonlyArray<{ key: string; value: number }> | undefined, keys: string[]): number[] {
  const byKey = new Map((rows ?? []).map((r) => [r.key, Number(r.value) || 0]));
  return keys.map((k) => byKey.get(k) ?? 0);
}

/** The first day a bucket key stands for, for labelling it. */
function bucketStartMs(key: string, granularity: Granularity): number {
  if (granularity === 'month') return dayMs(`${key}-01`);
  if (granularity === 'week') {
    const [y, w] = key.split('-W').map(Number);
    const jan4 = Date.UTC(y, 0, 4);
    const monday = jan4 - ((new Date(jan4).getUTCDay() + 6) % 7) * DAY;
    return monday + (w - 1) * 7 * DAY;
  }
  return dayMs(key);
}

/** "12 Sep", "Sep 2026" — a bucket as a reader names it, in the given language. */
export function bucketLabel(key: string, granularity: Granularity, locale = 'en'): string {
  const date = new Date(bucketStartMs(key, granularity));
  if (Number.isNaN(date.getTime())) return key;
  const options: Intl.DateTimeFormatOptions =
    granularity === 'month' ? { month: 'short', year: 'numeric', timeZone: 'UTC' } : { day: 'numeric', month: 'short', timeZone: 'UTC' };
  try {
    return new Intl.DateTimeFormat(locale, options).format(date);
  } catch {
    return key;
  }
}

/**
 * The points a TrendChart draws: this period's buckets, each with the
 * previous period's bucket at the same position behind it. The two windows
 * are equal in days, so position — not date — is what lines them up.
 */
export function trendPoints(
  current: ReadonlyArray<{ key: string; value: number }> | undefined,
  previous: ReadonlyArray<{ key: string; value: number }> | undefined,
  period: AnalyticsPeriod,
  granularity: Granularity,
  locale = 'en',
): TrendPoint[] {
  const keys = bucketKeys(period.startDate, period.endDate, granularity);
  const values = fillSeries(current, keys);
  const previousValues = previous ? fillSeries(previous, bucketKeys(period.previousStartDate, period.previousEndDate, granularity)) : null;
  return keys.map((key, i) => ({
    label: bucketLabel(key, granularity, locale),
    value: values[i],
    previous: previousValues ? previousValues[i] ?? null : undefined,
  }));
}
